import React, { useEffect, useMemo, useState } from "react";
import EntityConfiguration from "../../components/EntityConfiguration";
import EntityTypeAttributesManagement from "../../components/admin/EntityTypeAttributesManagement";
import EntityResolutionPolicyManagement from "../../components/admin/EntityResolutionPolicyManagement";
import ReviewWorkflowPolicyManagement from "../../components/admin/ReviewWorkflowPolicyManagement";
import EntityImportWizard from "../../components/admin/EntityImportWizard";
import ArtifactTemplatesManagement from "../../components/admin/ArtifactTemplatesManagement";
import UserManagement from "../../components/UserManagement";
import SuperUserManagement from "../../components/SuperUserManagement";
import TenantManagement from "../../components/TenantManagement";
import TaxonomiesHierarchyPanel from "../../components/TaxonomiesHierarchyPanel";
import DatabaseCleanup from "../../components/DatabaseCleanup";
import CompanySsoSettings from "../../components/admin/CompanySsoSettings";
import SecurityEventsExport from "../../components/admin/SecurityEventsExport";
import LlmUsagePanel from "../../components/admin/LlmUsagePanel";
import AgentWriteProposalsPanel from "../../components/AgentWriteProposalsPanel";
import { userIsAdministrator } from "../../utils/userRoles";
import { consumeAdminDeepLink } from "../../utils/adminDeepLink";
import { v2cx } from "../theme/tokens";
import { useV2Theme } from "../theme/ThemeProvider";

const GROUPS = [
  {
    id: "metamodel",
    label: "Map setup",
    desc: "Choose what kinds of things can appear on your enterprise map, and which extra fields they show.",
  },
  {
    id: "taxonomy",
    label: "Hierarchies",
    desc: "Organise organisation units, objectives, initiatives, processes, and capabilities into parent–child trees.",
  },
  {
    id: "governance",
    label: "AI map building",
    desc: "Control how the AI proposes, matches, and stages items when it builds your map from documents and notes.",
  },
  {
    id: "templates",
    label: "Document templates",
    desc: "Upload your organisation’s Word or Markdown templates for architecture blueprints. Word uploads keep headers, logos, and styles when documents are generated.",
  },
  {
    id: "access",
    label: "People & access",
    desc: "Invite colleagues and decide who can view or change the map.",
  },
  {
    id: "security",
    label: "Security & audit",
    desc: "Review agent write proposals and export security events for compliance.",
  },
  {
    id: "data_ops",
    label: "Import & cleanup",
    desc: "Bring items onto the map in bulk, or remove data you no longer need.",
  },
];

const V2AdminPage = () => {
  const { tokens } = useV2Theme();
  const [activeGroup, setActiveGroup] = useState("metamodel");
  const [activeTool, setActiveTool] = useState("entity_types");
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showDatabaseCleanup, setShowDatabaseCleanup] = useState(false);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const tools = useMemo(() => {
    const base = [
      {
        id: "entity_types",
        group: "metamodel",
        label: "Item types",
        desc: "Turn item types on or off and set how applications, capabilities, systems, and other map items behave.",
        render: () => <EntityConfiguration user={user} hideHeader mapLanguage />,
      },
      {
        id: "entity_attributes",
        group: "metamodel",
        label: "Custom fields",
        desc: "Add organisation-specific fields (for example owner, criticality, or cost centre) to each item type.",
        render: () => (
          <EntityTypeAttributesManagement user={user} hideHeader mapLanguage />
        ),
      },
      {
        id: "taxonomy_hierarchies",
        group: "taxonomy",
        label: "Build hierarchies",
        desc: "Create, nest, reparent, and import trees for organisation, objectives, initiatives, processes, and capabilities.",
        render: () => <TaxonomiesHierarchyPanel />,
      },
      {
        id: "resolution_policies",
        group: "governance",
        label: "Match or create",
        desc: "When the AI finds something in your documents, choose per item type: add it as new, link it to something already on the map, or discard it.",
        render: () => (
          <EntityResolutionPolicyManagement user={user} hideHeader mapLanguage />
        ),
      },
      {
        id: "review_policies",
        group: "governance",
        label: "Review before publish",
        desc: "Decide who must approve AI-proposed and imported items before they become part of your live map.",
        render: () => (
          <ReviewWorkflowPolicyManagement user={user} hideHeader mapLanguage />
        ),
      },
      {
        id: "artifact_templates",
        group: "templates",
        label: "Blueprint templates",
        desc: "Upload your organisation’s architecture blueprint template. Word uploads keep headers, logos, and styles.",
        render: () => <ArtifactTemplatesManagement hideHeader />,
      },
      {
        id: "user_management",
        group: "access",
        label: "Users & roles",
        desc: "Invite people and set whether they can view, edit, or administer the map.",
        render: () => <UserManagement user={user} hideHeader />,
      },
      {
        id: "company_sso",
        group: "access",
        label: "Company SSO",
        desc: "Connect Okta (OIDC) so colleagues sign in with your company identity provider.",
        render: () => <CompanySsoSettings hideHeader />,
      },
      {
        id: "agent_write_proposals",
        group: "security",
        label: "Agent write proposals",
        desc: "Accept or reject catalog writes queued by autonomous agent runs.",
        render: () => <AgentWriteProposalsPanel hideHeader />,
      },
      {
        id: "security_events",
        group: "security",
        label: "Security event export",
        desc: "Load and download tenant security events (SSO, SCIM, admin, proposals).",
        render: () => <SecurityEventsExport hideHeader />,
      },
      {
        id: "llm_usage",
        group: "security",
        label: "LLM usage & cost",
        desc: "Token consumption and estimated Vertex cost for this organisation.",
        render: () => <LlmUsagePanel hideHeader admin />,
      },
      {
        id: "entity_import",
        group: "data_ops",
        label: "Import from spreadsheet",
        desc: "Load many map items at once from an Excel or CSV file, with a guided mapping of columns to fields.",
        render: () => (
          <div className="p-4">
            <p className="text-sm text-slate-600 mb-3">
              Choose an item type, map your columns, then upload the file. You can
              review results before they land on the map. Generated templates
              include date format guidance, allowed dropdown values, and an
              Instructions sheet.
            </p>
            <p className="text-sm text-slate-600 mb-3">
              To import Organisation, Objective, Initiative, Business Context, or
              Business Capability trees, use the{" "}
              <span className="font-medium text-slate-800">Hierarchies</span>{" "}
              section instead — those types have a dedicated hierarchy import.
            </p>
            <button
              type="button"
              onClick={() => setShowImportWizard(true)}
              className={tokens.button.success}
            >
              Start import
            </button>
          </div>
        ),
      },
      {
        id: "clear_database",
        group: "data_ops",
        label: "Clear map data",
        desc: "Permanently remove selected item types—or the whole map. Use with care; this cannot be undone.",
        render: () => (
          <div className="p-4">
            <p className="text-sm text-slate-600 mb-3">
              Opens a confirmation dialog to clear selected item types or everything
              on your map. This cannot be undone.
            </p>
            <button
              type="button"
              onClick={() => setShowDatabaseCleanup(true)}
              className={tokens.button.danger}
            >
              Open clear map data
            </button>
          </div>
        ),
      },
    ];
    if (user?.is_super_user) {
      base.push(
        {
          id: "super_users",
          group: "access",
          label: "Super users",
          desc: "Manage global administrators who can work across organisations.",
          render: () => <SuperUserManagement user={user} />,
        },
        {
          id: "tenant_management",
          group: "access",
          label: "Organisations (tenants)",
          desc: "Create and manage customer organisations and their ownership.",
          render: () => <TenantManagement user={user} />,
        }
      );
    }
    return base;
  }, [tokens, user]);

  useEffect(() => {
    const toolId = consumeAdminDeepLink();
    if (!toolId) return;
    const match = tools.find((t) => t.id === toolId);
    if (match) {
      setActiveGroup(match.group);
      setActiveTool(match.id);
    }
  }, [tools]);

  const filteredTools = useMemo(
    () => tools.filter((t) => t.group === activeGroup),
    [activeGroup, tools]
  );

  const activeGroupMeta = useMemo(
    () => GROUPS.find((g) => g.id === activeGroup),
    [activeGroup]
  );

  const active = useMemo(() => {
    const match = tools.find((t) => t.id === activeTool);
    if (match) return match;
    const inGroup = tools.find((t) => t.group === activeGroup);
    return inGroup || tools[0] || null;
  }, [activeTool, activeGroup, tools]);

  const selectGroup = (groupId) => {
    setActiveGroup(groupId);
    const first = tools.find((t) => t.group === groupId);
    if (first) setActiveTool(first.id);
  };

  const selectTool = (tool) => {
    setActiveGroup(tool.group);
    setActiveTool(tool.id);
  };

  if (!userIsAdministrator(user)) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold text-slate-900">Admin</h1>
        <p className={`${tokens.surface.mutedText} text-sm`}>
          Only organization administrators can access Admin settings.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Admin</h1>
        <p className={`${tokens.surface.mutedText} mt-1 text-sm`}>
          Set up your map, AI behaviour, document templates, people, and data tools.
        </p>
      </div>

      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>Admin</span>
        <span className="text-slate-300">/</span>
        <span className="text-slate-600">{activeGroupMeta?.label || "Sections"}</span>
        {active ? (
          <>
            <span className="text-slate-300">/</span>
            <span className="text-slate-800 font-medium">{active.label}</span>
          </>
        ) : null}
      </div>

      <nav
        className={v2cx(tokens.surface.card, "overflow-hidden")}
        aria-label="Admin navigation"
      >
        <div className="px-3 pt-3 pb-2 border-b border-slate-200">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Sections
          </p>
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Admin sections">
            {GROUPS.map((g) => {
              const isActive = activeGroup === g.id;
              return (
                <button
                  key={g.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => selectGroup(g.id)}
                  className={v2cx(
                    "text-left px-3.5 py-2 rounded-lg border text-sm font-semibold transition-colors",
                    isActive
                      ? "border-red-500 bg-red-50 text-red-800 shadow-sm ring-1 ring-red-200"
                      : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="px-3 py-2.5 bg-slate-50/70 border-b border-slate-200"
          role="tabpanel"
          aria-label={`Tools in ${activeGroupMeta?.label || "section"}`}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Tools in
            </span>
            <span className="text-xs font-semibold text-slate-800">
              {activeGroupMeta?.label || "—"}
            </span>
          </div>
          {activeGroupMeta?.desc ? (
            <p className="text-sm text-slate-600 leading-relaxed mb-2.5 max-w-3xl">
              {activeGroupMeta.desc}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-1.5 pl-2 border-l-[3px] border-red-400 ml-0.5">
            {filteredTools.map((t) => {
              const isActive = active?.id === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => selectTool(t)}
                  className={v2cx(
                    "text-left px-3 py-1.5 rounded-md text-sm border transition-colors",
                    isActive
                      ? "border-red-500 bg-red-100 text-red-900 font-semibold shadow-sm ring-1 ring-red-200"
                      : "border-slate-200 bg-white/60 text-slate-600 hover:border-red-200 hover:bg-red-50/50"
                  )}
                  aria-current={isActive ? "page" : undefined}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      <div className={tokens.surface.card}>
        <div className={tokens.surface.panelHeader}>
          <h3 className="text-base font-semibold text-slate-900">
            {active?.label || "Select a tool"}
          </h3>
          <p className="text-xs text-slate-500 mt-1">{active?.desc || ""}</p>
        </div>
        <div className="v2-admin-light">
          {active?.id === "artifact_templates" ? (
            <ArtifactTemplatesManagement hideHeader />
          ) : active ? (
            active.render()
          ) : null}
        </div>
      </div>

      {showImportWizard ? (
        <EntityImportWizard
          variant="embedded"
          onClose={() => setShowImportWizard(false)}
        />
      ) : null}
      {showDatabaseCleanup ? (
        <DatabaseCleanup
          variant="embedded"
          onClose={() => setShowDatabaseCleanup(false)}
        />
      ) : null}
    </section>
  );
};

export default V2AdminPage;
